import queryString from 'query-string';

export const getUrlWithTrailingSlash = (url: string) => {
  const parsedUrl = queryString.parseUrl(url, { parseFragmentIdentifier: true });
  return queryString.stringifyUrl({ ...parsedUrl, url: parsedUrl.url.endsWith('/') ? parsedUrl.url : parsedUrl.url + '/' });
};

const absoluteUrlPattern = /^https?:\/\//i;
const withjoyDotComUrlPattern = /^https:\/\/withjoy.com\//i;

export const isExternalUrl = (url: string | undefined) => {
  return url && absoluteUrlPattern.test(url) && !withjoyDotComUrlPattern.test(url);
};

const externalRootUrlsMap: { readonly [key in string]: string } = {
  stage: 'https://rehearsal.withjoy.com',
  development: 'https://dev.withjoy.com',
  production: 'https://withjoy.com',
};

const internalRootUrlsMap: { readonly [key in string]: string } = {
  local: 'localhost:8000',
  stage: 'https://rehearsal.withjoy.com',
  development: 'https://newmarcom.withjoy.com',
  production: 'https://withjoy.com',
};

const blissGatewayUrlMap: { readonly [key in string]: string } = {
  local: 'https://bliss-gateway-dev.withjoy.com', // used for local development
  stage: 'https://bliss-gateway-prod.withjoy.com', // used on rehearsal.withjoy.com
  development: 'https://bliss-gateway-dev.withjoy.com', // what's used on newmarcom.withjoy.com
  production: 'https://bliss-gateway-prod.withjoy.com', // used on withjoy.com
};

const env = process.env.GATSBY_ENV || process.env.NODE_ENV!;

/**
 * get root url for non-marcom app, based on current marcom deployment env
 */
export const EXT_APP_ROOT_URL = externalRootUrlsMap[env] || externalRootUrlsMap['production'];

/**
 * get root url for internal marcom routes, based on current marcom deployment env
 */
export const INTERNAL_ROOT_URL = internalRootUrlsMap[env] || internalRootUrlsMap['production'];

/**
 * get root url for bliss gateway endpoints
 */
export const BLISS_GATEWAY_ROOT_URL = blissGatewayUrlMap[env] || internalRootUrlsMap['production'];

/**
 * Take url path: /login, /createwedding or /wedding-website and send to appropriate internal or external root url.
 */
export function getRootUrl(urlPath: string): string {
  const externalPaths: { readonly [key in string]: boolean } = {
    '/createwedding': true,
    '/login': true,
    '/amyplusdave': true,
    '/designs': true,
  };

  if (externalPaths[urlPath]) {
    return EXT_APP_ROOT_URL + urlPath;
  }

  return INTERNAL_ROOT_URL + urlPath;
}

const baseCreateWeddingUrl = getRootUrl('/createwedding');

const pageToScenarioMap: { [key in Exclude<MarcomPageName, 'home'>]: SignUpScenario } = {
  'wedding-invitations': 'invitation',
  'wedding-invitations-designs': 'invitation',
  'save-the-date': 'save-the-date',
  'save-the-date-designs': 'save-the-date',
  'wedding-website': 'wedding-website',
  'guest-list': 'guest-list',
  'find-a-couple': 'find-a-couple',
  registry: 'registry',
  app: 'app',
};

export const getCreateWeddingUrl = (fromPage: MarcomPageName | undefined) => {
  const resultUrl = queryString.parseUrl(baseCreateWeddingUrl);
  const queryParams = resultUrl.query;
  if (fromPage && fromPage !== 'home') {
    queryParams.scenario = pageToScenarioMap[fromPage];
  }
  return queryString.stringifyUrl(resultUrl);
};

export type SignUpScenario = 'invitation' | 'save-the-date' | 'registry' | 'app' | 'guest-list' | 'wedding-website' | 'find-a-couple';

export const getCardCreateWeddingUrl = (scenario: SignUpScenario, themeId: string) => {
  const resultUrl = queryString.parseUrl(baseCreateWeddingUrl);
  const queryParams = resultUrl.query;
  queryParams.scenario = scenario;
  queryParams.theme = themeId;
  queryParams.skipIfLoggedIn = 'true';
  return queryString.stringifyUrl(resultUrl);
};
